import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EmpLayout from "./emp_layout";
import { API_ENDPOINTS } from "../../api/config";
import './css/candidate_profile.css';

export function CandidateProfile() {
  const navigate = useNavigate();
  const { candidateId } = useParams(); 
  
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [shortlisted, setShortlisted] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interview, setInterview] = useState({ date: "", time: "", format: "Google Meet", notes: "" });
  const token = localStorage.getItem("token");

  // --- New State for Custom UI Confirmation Modal ---
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmLabel: "",
    variant: "primary", // primary, danger, gold
    onConfirm: () => {}
  });

  // Fetch candidate details
  const fetchCandidateDetails = useCallback(() => {
    if (!candidateId) {
      setError("No candidate ID provided.");
      setLoading(false);
      return;
    }

    fetch(`${API_ENDPOINTS.EMPLOYERCANDIDATES}/${candidateId}`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
    .then((res) => {
      if (!res.ok) throw new Error("Failed to fetch candidate profile details.");
      return res.json();
    })
    .then((data) => {
      // Defensive fallback if data itself returns empty/null
      if (!data) throw new Error("Candidate profile data is empty.");
      
      setCandidate(data);
      setShortlisted(data.shortListed || false);
      if (data.interview) {
        setInterview({
          date: data.interview.date || "",
          time: data.interview.time || "",
          format: data.interview.format || "Google Meet",
          notes: data.interview.notes || ""
        });
      }
      setLoading(false);
    })
    .catch((err) => {
      console.error("Error fetching candidate:", err);
      setError(err.message);
      setLoading(false);
    });
  }, [candidateId, token]);

  useEffect(() => {
    fetchCandidateDetails();
  }, [fetchCandidateDetails]);

  const setI = k => e => setInterview(i => ({ ...i, [k]: e.target.value }));
  
  const circumference = 2 * Math.PI * 28;
  const matchPct = candidate?.matchPercentage || 0;
  const offset = circumference - ((matchPct / 100) * circumference);

  // Centralized backend fetch execution helper
  const executeStatusUpdate = async (newStatus) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.EMPLOYERCANDIDATES}/${candidateId}/status`, {
        method: "PUT",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error("Failed to update application state status on the server.");
      
      // 1. Sync the local client UI state with the database's new state
      setCandidate(prev => prev ? { ...prev, applicationStatus: newStatus } : null);
      if (newStatus !== "Shortlisted") setShortlisted(false);
      
      // 2. Generate custom, friendly contextual strings for the success modal
      const candidateName = candidate?.name || "the candidate";
      let successTitle = "Success!";
      let successMessage = `The candidate's status has been updated to '${newStatus}'.`;
      let modalVariant = "primary"; // Default green accent style

      if (newStatus === "Offered") {
        successTitle = "Offer Extended! 🎉";
        successMessage = `An official job offer has been registered for ${candidateName}.`;
        modalVariant = "primary"; 
      } else if (newStatus === "Rejected") {
        successTitle = "Application Archived";
        successMessage = `You have successfully passed on ${candidateName}. Their status is now marked as Rejected.`;
        modalVariant = "danger"; // Accent style updates to match a rejection flow
      } else if (newStatus === "Under Review") {
        successTitle = "Candidate Restored";
        successMessage = `${candidateName} has been returned to the 'Under Review' pool.`;
        modalVariant = "gold";
      }

      // 3. Dispatch the dynamic custom modal layout view
      openActionModal(
        successTitle,
        successMessage,
        "Dismiss",
        modalVariant,
        () => closeActionModal()
      );

    } catch (err) {
      openActionModal("Error Updating Status", err.message, "Understood", "danger", () => closeActionModal());
    }
  };

  const handleViewCV = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.EMPLOYERCANDIDATES}/view-cv/${candidateId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("Session expired or unauthorized access.");
        throw new Error("Could not locate the document asset on the server.");
      }

      const fileBlob = await response.blob();
      const secureBlobUrl = window.URL.createObjectURL(fileBlob);
      window.open(secureBlobUrl, "_blank");
      
      setTimeout(() => window.URL.revokeObjectURL(secureBlobUrl), 200);      
    } catch (error) {
      openActionModal("Error", error.message || "An error occurred.", "Close", "danger", () => closeActionModal());
    }
  };

  // Intermediary UI orchestrator configurer instead of window.confirm
  const handleActionTrigger = (workflowType) => {
    const candidateName = candidate?.name || "this candidate";

    if (workflowType === "Rejected") {
      openActionModal(
        "Pass on Candidate?",
        `Are you certain you want to disqualify ${candidateName}? Their status will be visibly modified to 'Rejected'.`,
        "Yes, Pass on Candidate",
        "danger",
        () => executeStatusUpdate("Rejected")
      );
    } else if (workflowType === "Offered") {
      openActionModal(
        "Extend Official Job Offer?",
        `You are about to transition ${candidateName} to the 'Offered' phase. This typically communicates intentions to dispatch contract documents.`,
        "Confirm & Send Offer",
        "primary",
        () => executeStatusUpdate("Offered")
      );
    } else if (workflowType === "Under Review") {
      openActionModal(
        "Reconsider Candidate?",
        `Move ${candidateName} back into the 'Under Review' status pipeline tier?`,
        "Restore Candidate",
        "gold",
        () => executeStatusUpdate("Under Review")
      );
    }
  };

  const openActionModal = (title, message, confirmLabel, variant, onConfirm) => {
    setActionModal({ isOpen: true, title, message, confirmLabel, variant, onConfirm });
  };

  const closeActionModal = () => {
    setActionModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleToggleShortlist = async () => {
    const nextState = !shortlisted;
    const targetStatus = nextState ? "Shortlisted" : "Under Review";
    
    try {
      const response = await fetch(`${API_ENDPOINTS.EMPLOYERCANDIDATES}/${candidateId}/status`, {
        method: "PUT",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: targetStatus })
      });

      if (!response.ok) throw new Error("Failed to change selection status.");
      
      setShortlisted(nextState);
      setCandidate(prev => prev ? { ...prev, applicationStatus: targetStatus } : null);
    } catch (err) {
      openActionModal("Operational Failure", err.message, "Close", "danger", () => closeActionModal());
    }
  };

  const currentStatusClean = (candidate?.applicationStatus || "").replace(/\s+/g, '').toLowerCase();
  const isInterviewScheduled = currentStatusClean === "interviewscheduled";
  const isOffered = currentStatusClean === "offered";
  const isRejected = currentStatusClean === "rejected";

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    if (!interview.date || !interview.time) {
      openActionModal("Missing Fields", "Please designate a formal date and time window.", "Fix Issues", "gold", () => closeActionModal());
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.EMPLOYERCANDIDATES}/${candidateId}/schedule`, {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(interview)
      });

      if (!response.ok) throw new Error("Could not process interview registration.");

      setCandidate(prev => prev ? { ...prev, applicationStatus: "Interview Scheduled" } : null);
      setScheduleModal(false);

      openActionModal(
        "Success!",
        `The interview with ${candidate?.name || "the candidate"} has been successfully scheduled and registered.`,
        "Awesome",
        "primary",
        () => closeActionModal()
      );
    } catch (err) {
      openActionModal("Operational Failure", err.message, "Close", "danger", () => closeActionModal());
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <EmpLayout title="Candidate" titleEm="Profile">
        <div style={{ padding: "2rem", textAlign: "center", color: "#aaa" }}>
          Loading candidate profile details...
        </div>
      </EmpLayout>
    );
  }

  if (error || !candidate) {
    return (
      <EmpLayout title="Candidate" titleEm="Profile">
        <div style={{ padding: "2rem", textAlign: "center", color: "#d9534f" }}>
          <p>{error || "Candidate record could not be found."}</p>
          <button className="emp-btn-ghost" onClick={() => navigate(-1)}>Back to Applicants</button>
        </div>
      </EmpLayout>
    );
  }

  return (
    <EmpLayout title="Candidate" titleEm="Profile">
      <button style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:"0.78rem", display:"flex", alignItems:"center", gap:"0.4rem", padding:0, marginBottom:"1.25rem" }}
        onClick={() => navigate(-1)}>← Back to applicants</button>

      <div className="cp-layout">
        <div>
          {/* Hero segment */}
          <div className="cp-hero">
            <div className="cp-big-av">
              {candidate.name 
                ? candidate.name.trim().split(/\s+/).map(n => n[0]).join("").toUpperCase() 
                : "??"}
            </div>
            <div className="cp-hero-info">
              <div className="cp-hero-name">{candidate.name || "Anonymous Candidate"}</div>
              <div className="cp-hero-spec">
                {candidate.headline || `${candidate.specialisation || "N/A"} · ${candidate.experience || "No"} experience · ${candidate.location || "Remote"}`}
              </div>
              <div className="cp-hero-tags">
                <span className="cp-hero-tag">📍 {candidate.location || "N/A"}</span>
                <span className="cp-hero-tag">{candidate.availability || "Available"}</span>
                <span className={`cp-hero-tag ${isOffered ? 'green' : isRejected ? 'red' : 'gold'}`}>
                  Status: {candidate.applicationStatus || "Under Review"}
                </span>
              </div>
            </div>
          </div>

          {/* About */}
          <div className="cp-section">
            <div className="cp-section-label">About</div>
            <p className="cp-bio">{candidate.bio || "No professional overview provided."}</p>
          </div>

          {/* Application Cover Note Container */}
          <div className="cp-section" style={{ background: "#fafafa", borderLeft: "3px solid var(--color-primary)", padding: "1.25rem", borderRadius: "0 8px 8px 0", marginTop: "1.5rem" }}>
            <div className="cp-section-label" style={{ color: "var(--color-primary)", marginBottom: "0.5rem" }}>Application Cover Note</div>
            {candidate.coverNote ? (
              <p className="cp-bio" style={{ whiteSpace: "pre-wrap", fontStyle: "italic", color: "#4a4a4a", fontSize: "0.9rem", lineHeight: "1.6", margin: 0 }}>
                "{candidate.coverNote}"
              </p>
            ) : (
              <span style={{ color: "#aaa", fontSize: "0.85rem", fontStyle: "italic" }}>The candidate did not include a cover note with this application.</span>
            )}
          </div>

          {/* Details */}
          <div className="cp-section">
            <div className="cp-section-label">Details</div>
            <div className="cp-grid2">
              {[
                ["Specialisation", candidate.specialisation],
                ["Experience", candidate.experience],
                ["Location", candidate.location],
                ["Availability", candidate.availability],
              ].map(([k, v]) => (
                <div key={k} className="cp-detail">
                  <div className="cp-detail-l">{k}</div>
                  <div className="cp-detail-v">{v || "N/A"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="cp-section">
            <div className="cp-section-label">Skills</div>
            <div className="cp-skill-wrap">
              {Array.isArray(candidate.skills) && candidate.skills.length > 0 ? (
                candidate.skills.map(s => (
                  <span key={s} className="cp-skill">{s}</span>
                ))
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.85rem" }}>No skills listed.</span>
              )}
            </div>
          </div>

          {/* Certifications Display Module */}
          <div className="cp-section">
            <div className="cp-section-label">Certifications</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {Array.isArray(candidate.certificates) && candidate.certificates.length > 0 ? (
                candidate.certificates.map((cert, index) => (
                  <div key={cert.id || index} style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", background: "rgba(var(--color-text-rgb),0.02)", padding: "0.85rem 1rem", borderRadius: "6px" }}>
                    <span style={{ fontSize: "1.2rem", lineHeight: 1 }}>📜</span>
                    <div>
                      <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "var(--color-text)" }}>{cert.certificateName || "Certified Qualification"}</div>
                      <div style={{ fontSize: "0.78rem", color: "#aaa", marginTop: "0.15rem" }}>{cert.issuingOrganization || "Unknown Institution"}</div>
                    </div>
                  </div>
                ))
              ) : (
                <span style={{ color: "#aaa", fontSize: "0.85rem", fontStyle: "italic" }}>No certified qualifications listed.</span>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar Controls Section */}
        <div>
          <div className="cp-sidebar-card">
            <div className="cp-sidebar-title">Actions</div>
            
            {/* View CV Trigger */}
            {candidate.cvUrl ? (
              <button
                type="button"
                onClick={handleViewCV} 
                className="cp-action-btn ghost" 
                style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "0.5rem", width: "100%" }}
              >
                📄 View Curriculum Vitae (CV)
              </button>
            ) : (
              <button className="cp-action-btn ghost" disabled style={{ opacity: 0.5, cursor: "not-allowed", marginBottom: "0.5rem" }}>
                📄 No CV Uploaded
              </button>
            )}

            {/* Context-aware primary hiring layout flows */}
            {!isInterviewScheduled && !isOffered && !isRejected && (
              <button className="cp-action-btn primary" style={{ marginBottom: "0.5rem" }} onClick={() => setScheduleModal(true)}>
                📅 Schedule Interview
              </button>
            )}

            {isInterviewScheduled && (
              <>
                <button className="cp-action-btn gold" onClick={() => setScheduleModal(true)} style={{ marginBottom: "0.5rem" }}>
                  🔄 Reschedule Interview
                </button>
                <button className="cp-action-btn primary" onClick={() => handleActionTrigger("Offered")} style={{ marginBottom: "0.5rem", backgroundColor: "#10b981", color: "#fff" }}>
                  🎉 Extend Job Offer
                </button>
              </>
            )}

            {isOffered && (
              <div style={{ background: "#ecfdf5", color: "#065f46", padding: "0.6rem", borderRadius: "6px", fontSize: "0.8rem", textAlign: "center", fontWeight: "500", marginBottom: "0.5rem" }}>
                Proposed offer letter extended
              </div>
            )}

            {/* General Workflow modifications allowed outside terminal closure */}
            {!isRejected && (
              <button className={`cp-action-btn${shortlisted ? " gold" : " ghost"}`} style={{ marginBottom: "0.5rem" }} onClick={handleToggleShortlist}>
                {shortlisted ? "♥ Shortlisted" : "♡ Add to Shortlist"}
              </button>
            )}

            {!isRejected ? (
              <button className="cp-action-btn danger" onClick={() => handleActionTrigger("Rejected")}>
                ✕ Pass on Candidate
              </button>
            ) : (
              <button className="cp-action-btn ghost" onClick={() => handleActionTrigger("Under Review")} style={{ color: "#4b5563" }}>
                Reconsider Candidate (Restore)
              </button>
            )}
          </div>

          {isInterviewScheduled && candidate.interview && (
            <div className="cp-section" style={{ background: "#fdfbf7", border: "1px solid #eaddc5", padding: "1.5rem", borderRadius: "8px", marginTop: "1.5rem" }}>
              <div className="cp-section-label" style={{ color: "var(--color-primary)", marginBottom: "1rem" }}>
                📅 Scheduled Interview Details
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
                <div>
                  <span style={{ fontSize: "0.8rem", color: "#aaa", display: "block" }}>Date & Time</span>
                  <strong style={{ color: "var(--color-text)" }}>
                    {(candidate.interview.date || candidate.interview.time) 
                      ? `${candidate.interview.date || ""} @ ${candidate.interview.time || ""}` 
                      : "Pending setup"}
                  </strong>
                </div>
                <div>
                  <span style={{ fontSize: "0.8rem", color: "#aaa", display: "block" }}>Format Structure</span>
                  <strong style={{ color: "var(--color-text)" }}>{candidate.interview.format || "N/A"}</strong>
                </div>
                {candidate.interview.meetingLink && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span style={{ fontSize: "0.8rem", color: "#aaa", display: "block" }}>Meeting URL</span>
                    <a href={candidate.interview.meetingLink} target="_blank" rel="noreferrer" style={{ color: "var(--color-primary)", fontWeight: 500, fontSize: "0.9rem", textDecoration: "underline" }}>
                      {candidate.interview.meetingLink}
                    </a>
                  </div>
                )}
                {candidate.interview.notes && (
                  <div style={{ gridColumn: "1 / -1", background: "rgba(0,0,0,0.02)", padding: "0.75rem", borderRadius: "4px", fontSize: "0.85rem", color: "#555", fontStyle: "italic" }}>
                    "{candidate.interview.notes}"
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Schedule interview modal */}
      {scheduleModal && (
        <div className="cp-schedule-modal-bg" onClick={e => e.target === e.currentTarget && setScheduleModal(false)}>
          <form className="cp-schedule-modal" onSubmit={handleScheduleInterview}>
            <div className="cp-schedule-title">Schedule <em>Interview</em></div>
            <div className="cp-schedule-sub">With {candidate.name || "Candidate"} · {candidate.appliedRole || "Applicant"}</div>
            <div className="emp-field"><label className="emp-label">Date</label><input className="emp-input" type="date" required value={interview.date} onChange={setI("date")} /></div>
            <div className="emp-grid2">
              <div className="emp-field"><label className="emp-label">Time</label><input className="emp-input" type="time" required value={interview.time} onChange={setI("time")} /></div>
              <div className="emp-field"><label className="emp-label">Format</label>
                <select className="emp-select" value={interview.format} onChange={setI("format")}>
                  {["Google Meet","Zoom","In-person","Phone Call"].map(f=><option key={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div className="emp-field"><label className="emp-label">Notes for candidate (optional)</label><textarea className="emp-textarea" rows={3} placeholder="Any preparation notes or instructions…" value={interview.notes} onChange={setI("notes")} style={{ minHeight:80 }} /></div>
            <div className="cp-modal-actions">
              <button type="button" className="emp-btn-ghost" disabled={isSubmitting} onClick={() => setScheduleModal(false)}>Cancel</button>
              <button type="submit" className="emp-btn-gold" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Invite"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* --- Dynamic User-Friendly Confirmation Action Modal --- */}
      {actionModal.isOpen && (
        <div className="cp-schedule-modal-bg" onClick={closeActionModal}>
          <div className="cp-schedule-modal" style={{ maxWidth: "440px" }} onClick={e => e.stopPropagation()}>
            <div className="cp-schedule-title" style={{ fontSize: "1.4rem", marginBottom: "0.5rem" }}>
              {actionModal.title}
            </div>
            <p style={{ color: "#4a4a4a", fontSize: "0.9rem", lineHeight: "1.5", marginBottom: "1.5rem" }}>
              {actionModal.message}
            </p>
            <div className="cp-modal-actions" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
              <button type="button" className="emp-btn-ghost" onClick={closeActionModal}>
                Cancel
              </button>
              <button 
                type="button" 
                className={`cp-action-btn ${actionModal.variant}`}
                style={{ 
                  padding: "0.55rem 1.25rem", 
                  width: "auto", 
                  fontSize: "0.85rem", 
                  margin: 0,
                  backgroundColor: actionModal.variant === "danger" ? "#d9534f" : actionModal.variant === "primary" ? "#10b981" : "var(--color-primary)",
                  color: "#fff"
                }} 
                onClick={actionModal.onConfirm}
              >
                {actionModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </EmpLayout>
  );
}